import { ColumnSource, RowSource } from "@omegagrid/grid";
import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { AccountingOptions } from "@reporting/rp-types/core";
import { ModuleCode, MODULE_CODES } from "@reporting/rp-types/accounting";
import { PropertiesDefinition, StatementDefinition } from "@reporting/rp-types/definition";
import { CustomDetailProps } from "../types";
import { TreeSource } from "@omegagrid/tree";

export class DetailCustomGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private propName: keyof AccountingOptions;

	constructor (
		private moduleCode: ModuleCode,
	) {
		super();
		this.propName = moduleCode == MODULE_CODES.FINANCE ? 'fin_custom_detail' : 'item_custom_detail';
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}, {
				action: 'duplicate',
				title: msg('Duplicate'),
				icon: 'copy',
				color: ColorTypes.Yellow
			}],
			filter: false,
			sorting: false,
		}, {
			data: 'name',
			header: msg('Name'),
			t: 't',
			e: true,
			w: 200
		}, {
			data: 'id_custom_resource',
			header: msg('Custom'),
			t: 's',
			e: true,
			w: 300,
			content: await this.getCustomsList()
		}]
		if (this.moduleCode == MODULE_CODES.FINANCE) {
			this._columns.push({
				data: 'id_statement',
				header: msg('Report'),
				t: 's',
				e: true,
				w: 200,
				content: await this.getReportsList()
			});
		}
		return this._columns;
	}

	async getCustomsList(): Promise<TreeSource> {
		const list = await rp.ms('extensions').get<TreeSource>('resourcesTree');
		return list;
	}

	async getReportsList(): Promise<{ k: string|number, v: string }[]> {
		const statements = await rp.ms('definition').get<StatementDefinition[]>('statements');
		return statements.map(item => ({ k: item.id_statement.toString(), v: item.statement }));
	}

	async getRows(): Promise<RowSource[]> {
		const options = await rp.ms('accounting').get<AccountingOptions>('options');
		const props = options[this.propName] as CustomDetailProps[];
		if (!props) return [];
		return props.map(data => ({ data: {
			name: data.name,
			id_custom_resource: data.id_custom_resource?.toString(),
			id_statement: data.id_statement?.toString()
		}}));
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			refreshAfterSave: true,
			filters: true,
			saveMode: 'all_rows',
			pageSize: null,
			showEmptyRow: true,
			rowMenu: {
				addRows: true,
				deleteRows: true,
			},
		});
	}

	async save(rowData: RowData[]) {
		let data: PropertiesDefinition[] = rowData
			.filter(row => row.state !== 'deleted')
			.map(row => Object.assign(row.data));
		if (data.length === 0) {
			data = null;
		} else {
			const names = data.map(item => item.name);
			const uniqueNames = new Set(names);
			if (names.length !== uniqueNames.size) {
				rp.host.bucket.showAlert({content: msg('Names must be unique!'), type: 'warning'});
				return Promise.reject('Names must be unique');
			}
			if (data.some(item => item.name.trim() === '')) {
				rp.host.bucket.showAlert({content: msg('Name cannot be empty!'), type: 'warning'});
				return Promise.reject('Name cannot be empty');
			}
		}
		const newOptions: AccountingOptions = { [this.propName]: data };
		await rp.ms('accounting').post(`options`, newOptions);
	}

}
